/**
 * A wall may not end inside an opening (PRP-SKETCH-FIELD-4 K7, N7; Xactimate treats it as
 * invalid).
 *
 * - A missing wall (a partial one, an opening in a wall that stays) that another wall now
 *   meets splits at that point into two missing walls, one per segment (K6). Both copies
 *   of a shared one split; each piece is linked to the room across it. A removed wall or a
 *   split line (it carries the J1 record, `missing.absorbed`) is the whole wall and is not
 *   split (DD-SF4.10). Run after every change that can move a T (`splitMissingAtJunctions`).
 * - A door or window: the moving wall stops at the nearer jamb, its face on the jamb
 *   (`jambStop`), with `JAMB_MESSAGE`. Within `jamb_snap_in` outside a jamb it snaps onto
 *   the jamb too (a wall ends flush at the casing, not an inch past it).
 */
import { JAMB_SNAP_FT, POLICY, inToFt } from './policy';
import { EPS, clone, edgeOf, round6, unit } from './geom';
import { buildWallGraph, type WallGraph } from './graph';
import { addOpeningAt } from './openings';
import { edgeJunctions } from './segments';
import type { Axis, Opening, Plan, Room } from './types';

export const JAMB_MESSAGE = "A wall can't end inside a door or window, so it stops at the jamb. To put the wall there, move the door first.";

/** A piece shorter than one snap step is not split off. */
const MIN_PIECE_FT = inToFt(POLICY.snap_unit_in);

/** Split opening `o` of `r` at offset `s` (both copies; pieces relinked to whatever room is across each). */
function splitOpening(plan: Plan, r: Room, o: Opening, s: number): number[] {
  const spec = clone(o);
  const { id, offFt: o0, widthFt: w } = o;
  delete (spec as Partial<Opening>).id;
  delete (spec as Partial<Opening>).link;
  delete (spec as Partial<Opening>).edgeStart;
  delete (spec as Partial<Opening>).offFt;
  // Every copy goes (the one across moves with whatever room is across each piece now).
  if (o.link != null) plan.rooms.forEach((q) => { q.openings = q.openings.filter((x) => x.link !== o.link || q === r); });
  r.openings = r.openings.filter((x) => x !== o);
  const a = addOpeningAt(plan, r, o.edgeStart, o0, { ...spec, widthFt: round6(s - o0) });
  r.openings.find((x) => x.id === a.oid)!.id = id; // the first piece keeps the opening's id
  const b = addOpeningAt(plan, r, o.edgeStart, round6(s), { ...spec, widthFt: round6(o0 + w - s) });
  return [id, b.oid];
}

/**
 * K7 for missing walls: every partial missing wall another wall meets strictly inside
 * is split there. Returns the ids of the openings split (first pieces).
 */
export function splitMissingAtJunctions(plan: Plan, G?: WallGraph): number[] {
  const split: number[] = [];
  // Each pass splits one opening at one T, and a T is a room's corner: the bound only guards a bug.
  const passes = plan.rooms.reduce((n, r) => n + r.openings.length + r.corners.length, 0) + 1;
  for (let guard = 0; guard < passes; guard++) {
    const g = G && !split.length ? G : buildWallGraph(plan);
    let done = false;
    for (const r of plan.rooms) {
      for (const c of r.corners) {
        const J = edgeJunctions(plan, r.id, c.id, g);
        if (!J.length) continue;
        for (const o of r.openings) {
          if (o.edgeStart !== c.id || o.kind !== 'missing' || o.missing?.absorbed?.length) continue;
          const j = J.find((x) => x.s > o.offFt + MIN_PIECE_FT && x.s < o.offFt + o.widthFt - MIN_PIECE_FT);
          if (!j) continue;
          split.push(splitOpening(plan, r, o, j.s)[0]);
          done = true;
          break;
        }
        if (done) break;
      }
      if (done) break;
    }
    if (!done) break;
  }
  return split;
}

export interface JambStop {
  /** Move to add to the moving line, feet on its axis. */
  shift: number;
  message: string;
}

/**
 * K7 for doors and windows: the T's the moving corners (`moving`, of room `rid`, whose
 * wall is `thickFt` thick) make on other rooms' walls. When one lands inside a door or
 * window (the wall's half thickness either side counts), or within `jamb_snap_in`
 * outside its jamb, the shift that puts the wall's face on the nearer jamb. Null when
 * every T is clear.
 */
export function jambStop(plan: Plan, rid: string, moving: Set<number>, ax: Axis, thickFt: number): JambStop | null {
  const G = buildWallGraph(plan);
  const half = thickFt / 2;
  for (const q of plan.rooms) {
    for (const c of q.corners) {
      const E = edgeOf(q, c.id);
      if (!E) continue;
      const u = unit(E.a, E.b);
      if (Math.abs(u[ax]) < 1 - EPS) continue; // only walls running along the move
      for (const j of edgeJunctions(plan, q.id, c.id, G)) {
        if (!j.meet || j.meet.rid !== rid || !moving.has(j.meet.cid)) continue;
        for (const o of q.openings) {
          if (o.edgeStart !== c.id || o.kind === 'missing') continue;
          const lo = o.offFt - half, hi = o.offFt + o.widthFt + half;
          const inside = j.s > lo + EPS && j.s < hi - EPS;
          const near = Math.min(Math.abs(j.s - lo), Math.abs(j.s - hi)) <= JAMB_SNAP_FT + EPS;
          if (!inside && !near) continue;
          const to = Math.abs(j.s - lo) <= Math.abs(j.s - hi) ? lo : hi;
          if (Math.abs(to - j.s) < EPS) continue;
          return { shift: (to - j.s) * u[ax], message: inside ? JAMB_MESSAGE : '' };
        }
      }
    }
  }
  return null;
}
